import type { AdType, PaidSearchBreakdownType } from '@/api/ad';
import type {
  ALL_PAID_SEARCH_SEARCH_PARAM_KEYS,
  PAID_SEARCH_SORT_KEYS,
} from '@/router/searchParams/paidSearchSearchParams';
import type { NavigableRouteType, RouteSearchParamEnumType } from '@/router/types';

import { instance } from './appRoutes';

export const paidSearch = {
  path: `${instance.path}/paid-search`,
  description:
    "A list of paid search ad groups or keywords (an ad group contains one or more keywords). Grants the ability to toggle between ad groups and keywords and view the metrics for each. Contains the following metrics for each ad group or keyword: spend, impressions, clicks, visits, visit efficiency, cost per visit, engaged companies, attribution metrics, and fit score. Fit score is Channel99's proprietary algorithm that measures how well the ad group or keyword is performing relative to other ad groups or keywords for a given audience.",
  navigable: true,
  pathParams: {},
  searchParams: {
    type: {
      type: 'enum',
      enumValues: ['adGroup', 'keyword'] satisfies PaidSearchBreakdownType[],
      default: 'adGroup',
      description: 'The type of paid search objects to display',
    } satisfies RouteSearchParamEnumType<'adGroup' | 'keyword'>,
    'p.audience': {
      type: 'string',
      description: 'An audience ID to filter the paid search ad groups or keywords by',
      requiresLookup: true,
      lookupType: 'audience',
    },
    'p.adGroupId': {
      type: 'string',
      description: 'Ad group IDs to filter the paid search ad groups or keywords by',
      requiresLookup: true,
      lookupType: 'adGroup',
      supportsNegation: true,
    },
    'p.ad.matchType': {
      type: 'enum',
      enumValues: ['Exact', 'Phrase', 'Broad'] satisfies AdType['matchType'][],
      description: 'The match type of the ad',
      required: true,
    },
    'p.keywordId': {
      type: 'string',
      description: 'Keyword IDs to filter keywords by (not applicable for ad group view)',
      requiresLookup: true,
      lookupType: 'ad',
      supportsNegation: true,
    },
    'p.campaign.id': {
      type: 'string[]',
      description: 'Campaign IDs to filter the paid search ad groups or keywords by',
      requiresLookup: true,
      lookupType: 'campaign',
      supportsNegation: true,
    },
    'p.vendor.id': {
      type: 'string[]',
      description: 'Vendor IDs to filter the paid search ad groups or keywords by',
      requiresLookup: true,
      lookupType: 'vendor',
      supportsNegation: true,
    },
    'p.spend': {
      type: 'range',
      description: 'A range of spend from a min to max value.',
    },
    'p.spendPerTargetVisit': {
      type: 'range',
      description: 'A range of cost per target visit from a min to max value.',
    },
    'p.adImpressions': {
      type: 'range',
      description: 'A range of ad impressions from a min to max value.',
    },
    'p.adClicks': {
      type: 'range',
      description: 'A range of ad clicks from a min to max value.',
    },
    'p.viewThroughVisits': {
      type: 'range',
      description: 'A range of view-through visits from a min to max value.',
    },
    'p.visits': {
      type: 'range',
      description: 'A range of visits from a min to max value.',
    },
    'p.viewThroughPercent': {
      type: 'range',
      description:
        'A range of view-through percentage from a min to max value, represented as a decimal between 0 and 1 (for 0% to 100%).',
    },
    'p.visitEfficiency': {
      type: 'range',
      description: 'A range of visit efficiency from a min to max value.',
    },
    'p.engagedCompanies': {
      type: 'range',
      description: 'A range of engaged companies from a min to max value.',
    },

    'p.leadsInfluenced': {
      type: 'range',
      description: 'A range of leads influenced from a min to max value.',
    },
    'p.pipelineInfluenced': {
      type: 'range',
      description: 'A range of pipeline influenced from a min to max value.',
    },
    'p.countPipelineInfluenced': {
      type: 'range',
      description: 'A range of pipeline opportunities influenced (count) from a min to max value.',
    },
    'p.closedWonInfluenced': {
      type: 'range',
      description: 'A range of closed-won influenced from a min to max value.',
    },
    'p.countClosedwonInfluenced': {
      type: 'range',
      description:
        'A range of count of closed-won opportunities influenced from a min to max value.',
    },
    'p.customImpactScore': {
      type: 'range',
      description: 'A range of impact score from a min to max value.',
    },
    'p.opportunityInfluencedSumPipelineCustom': {
      type: 'range',
      description: 'A range of impact score pipeline attribution from a min to max value.',
    },
    'p.opportunityInfluencedSumClosedWonCustom': {
      type: 'range',
      description: 'A range of impact score closed won attribution from a min to max value.',
    },
    'p.pipelineLinearAttribution': {
      type: 'range',
      description: 'A range of linear pipeline from a min to max value.',
    },
    'p.closedWonLinearAttribution': {
      type: 'range',
      description: 'A range of linear closed won from a min to max value.',
    },
    'p.pipelineFirstTouchAttribution': {
      type: 'range',
      description: 'A range of first touch pipeline from a min to max value.',
    },
    'p.closedWonFirstTouchAttribution': {
      type: 'range',
      description: 'A range of first touch closed won from a min to max value.',
    },
    'p.pipelineLastTouchAttribution': {
      type: 'range',
      description: 'A range of last touch pipeline from a min to max value.',
    },
    'p.sortBy': {
      type: 'enum',
      enumValues: [
        'ad.name',
        'ad.matchType',
        'adGroup.name',
        'campaign.id',
        'vendor.id',
        'spend',
        'spendPerTargetVisit',
        'adImpressions',
        'adClicks',
        'visits',
        'viewThroughVisits',
        'viewThroughPercent',
        'visitEfficiency',
        'customImpactScore',
        'engagedCompanies',
        'leadsInfluenced',
        'pipelineFirstTouchAttribution',
        'pipelineLastTouchAttribution',
        'opportunityInfluencedSumClosedWonCustom',
        'opportunityInfluencedSumPipelineCustom',
        'pipelineLinearAttribution',
        'pipelineInfluenced',
        'countPipelineInfluenced',
        'closedWonFirstTouchAttribution',
        'closedWonLinearAttribution',
        'closedWonInfluenced',
        'countClosedwonInfluenced',
      ],
      default: 'visits',
      description: 'The field to sort by',
    } satisfies RouteSearchParamEnumType<PAID_SEARCH_SORT_KEYS>,
    'p.sortOrder': {
      type: 'enum',
      enumValues: ['asc', 'desc'],
      default: 'desc',
      description: 'The sort order',
    },
    'p.page': {
      type: 'string',
      description: 'The page number to display',
    },
    'p.size': {
      type: 'string',
      description: 'The number of items to display per page',
    },
  },
} satisfies NavigableRouteType<ALL_PAID_SEARCH_SEARCH_PARAM_KEYS>;

export const paidSearchByType = (type: 'adGroup' | 'keyword') => {
  return `${paidSearch.path}?type=${type}`;
};
